import PageFormWrapper from '../../../components/layouts/PageFormWrapper';
import InputField from '../../../components/Forms/Input/InputField';
import SelectField from '../../../components/Forms/Input/SelectField';
import DatePicker from '../../../components/Forms/DatePicker/DatePicker';
import SimpleSearchSelect from '../../../components/Forms/Input/SimpleSearchSelect';
import { useDocumentType } from '../../../hooks/useDocumentType';
import { useEffect } from 'react';
import { PurchaseFormValues } from '../../../types/purchases/BasePurchase';
import { useSupplier } from '../../../hooks/useSupplier';
import SvgIcon from '../../SvgIcon/SvgIcon';
import { useBasePurchase } from '../../../hooks/purchases/useBasePurchase';

interface Props {
  form: PurchaseFormValues;
  updateField: <K extends keyof PurchaseFormValues>(
    field: K,
    value: PurchaseFormValues[K]
  ) => void;
  formErrors: Record<string, string>;
}

const ImportDocumentInfoCard = ({ form, updateField, formErrors }: Props) => {
  const { fetchDocumentTypeDropdown, documentTypeDropdown } = useDocumentType();
  const { handlerGetSuppliersForDropdown, suppliersDropdown } = useSupplier();
  const { handlerUpdatePurchaseHeader, purchaseSettlementLoaded } =
    useBasePurchase();
  useEffect(() => {
    fetchDocumentTypeDropdown();
    handlerGetSuppliersForDropdown(); // ⬅️ Obtenés los proveedores
  }, []);

  return (
    <PageFormWrapper title="Compra de Importación">
      <div className="mb-6 grid gap-4 sm:grid-cols-2">
        <SelectField
          label="Tipo de Documento"
          name="documentType"
          iconKey="list"
          value={form.documentType?.id || ''}
          onChange={(e) =>
            updateField('documentType', {
              id: Number(e.target.value),
              name: e.target.options[e.target.selectedIndex].text,
            })
          }
          options={documentTypeDropdown
            .filter((doc) => doc.id !== 6 && doc.id !== 7)
            .map((doc) => ({
              value: doc.id,
              label: doc.name,
            }))}
          error={formErrors.documentType}
          required
          disabled={purchaseSettlementLoaded}
        />

        <InputField
          label="N° Documento"
          name="documentNumber"
          iconKey="hash"
          type="text"
          value={form.documentNumber || ''}
          onChange={(e) => updateField('documentNumber', e.target.value)}
          error={formErrors.documentNumber}
          required
          disabled={purchaseSettlementLoaded}
        />
      </div>

      <div className="mb-6 grid gap-4 sm:grid-cols-2">
        <DatePicker
          label="Fecha de Compra"
          name="purchaseDate"
          iconKey="calendar"
          value={form.purchaseDate || ''}
          onChange={(date) => updateField('purchaseDate', date)}
          error={formErrors.purchaseDate}
          required
          disabled={purchaseSettlementLoaded}
        />

        <SimpleSearchSelect
          label="Proveedor"
          name="supplier"
          iconKey="listOrdered"
          options={suppliersDropdown}
          selectedValue={form.supplier?.id || null}
          onSelect={(option) => updateField('supplier', option)}
          error={formErrors.supplier}
          required
          disabled={purchaseSettlementLoaded}
        />
      </div>

      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <InputField
          label="Monto Neto"
          name="netAmount"
          iconKey="dollar"
          type="number"
          value={form.netAmount || ''}
          onChange={(e) => updateField('netAmount', Number(e.target.value))}
          error={formErrors.netAmount}
          min={0}
          step={'0.01'}
          required
          disabled={purchaseSettlementLoaded}
        />
        <InputField
          label="Flete"
          name="freight"
          iconKey="truck"
          type="number"
          value={form.freight || ''}
          onChange={(e) => updateField('freight', Number(e.target.value))}
          error={formErrors.freight}
          min={0}
          step={'0.01'}
          required
          disabled={purchaseSettlementLoaded}
        />
        <InputField
          label="Seguro"
          name="insurance"
          iconKey="security"
          type="number"
          value={form.insurance || ''}
          onChange={(e) => updateField('insurance', Number(e.target.value))}
          error={formErrors.insurance}
          min={0}
          step={'0.01'}
          required
          disabled={purchaseSettlementLoaded}
        />
        <InputField
          label="FAUCA"
          name="fauca"
          iconKey="document"
          type="number"
          value={form.fauca || ''}
          onChange={(e) => updateField('fauca', Number(e.target.value))}
          error={formErrors.fauca}
          min={0}
          step={'0.01'}
          required
          disabled={purchaseSettlementLoaded}
        />
        <InputField
          label="Otros Costos"
          name="otherCosts"
          iconKey="more"
          type="number"
          value={form.otherCosts || ''}
          onChange={(e) => updateField('otherCosts', Number(e.target.value))}
          error={formErrors.otherCosts}
          min={0}
          step={'0.01'}
          disabled={purchaseSettlementLoaded}
        />
      </div>

      {/* Mostrar botón solo si NO está en modo asentamiento */}
      {!purchaseSettlementLoaded && (
        <div className="mt-6 flex justify-end">
          <button
            onClick={() => handlerUpdatePurchaseHeader(form.id!, form)}
            className="inline-flex items-center gap-2 rounded bg-primary px-4 py-2 text-white hover:bg-opacity-90"
          >
            <SvgIcon name="continue" className="h-5 w-5" />
            Actualizar Compra
          </button>
        </div>
      )}
    </PageFormWrapper>
  );
};

export default ImportDocumentInfoCard;
